/* eslint-disable max-lines-per-function */
/* eslint-disable max-statements */
import { Button, Center, Group, Loader, Text, useMantineTheme } from '@mantine/core';
import { modals } from '@mantine/modals';
import { IconCopy, IconDownload, IconEye, IconTrash, IconUpload } from '@tabler/icons-react';
import { isEqual } from 'lodash';
import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';

import { Template, TemplatesCriteria, TemplateType } from '@e-origin/shared';

import {
  getInitialTemplateCriteria,
  useAdminExportTemplate,
  useAdminTemplates,
  useDeclarants,
  useDeleteTemplate,
  useTemplateCriteriaUrlSync,
} from '@admin/hooks';
import {
  CheckedColumn,
  ListFilterChangeEvent,
  ListFilterDropdown,
  ListFilterDropdownElements,
  SearchBox,
  Table,
  TableActionEnum,
  TableActions,
} from '@components';
import { TABLE_MENU_THRESHOLD } from '@constants';
import { ContentLayout } from '@features/layout';
import { useSettings } from '@hooks/useSettings';
import { removeUndefined } from '@utils';
import { DuplicateTemplate } from './DuplicateTemplate';
import { ImportTemplate } from './ImportTemplate';
import { TemplateTypeOption, TemplateTypeView } from './TemplateTypeView';

const templateViewOptions: TemplateTypeOption[] = [
  { value: TemplateType.HIGH_VALUE_H1, label: 'Batch high value', 'data-pw': 'template-dropdown-batch-high-value' },
  { value: TemplateType.LOW_VALUE_H7, label: 'Batch low value', 'data-pw': 'template-dropdown-batch-low-value' },
  { value: TemplateType.DEPARTURE_TRANSIT, label: 'Transit departure', 'data-pw': 'template-dropdown-transit-departure' },
  { value: TemplateType.ARRIVAL_TRANSIT, label: 'Transit arrival', 'data-pw': 'template-dropdown-transit-arrival' },
  { value: TemplateType.COMBINED_TSD, label: 'Combined TSD', 'data-pw': 'template-dropdown-combined-tsd' },
  { value: TemplateType.TRANSFERRED_TSD, label: 'Transferred TSD', 'data-pw': 'template-dropdown-transferred-tsd' },
  { value: TemplateType.PRELODGED_TSD, label: 'Prelodged TSD', 'data-pw': 'template-dropdown-prelodged-tsd' },
  { value: 'other' as any, label: 'Other', 'data-pw': 'template-dropdown-other' },
];

const templateViewOtherOptions: TemplateTypeOption[] = [
  { value: TemplateType.CUSTOMS_REPORT, label: 'Customs report' },
  { value: TemplateType.BC_EAD_CUSTOMS_REPORT, label: 'EAD customs report' },
  { value: TemplateType.IMP_CUSTOMS_REPORT, label: 'IMP customs report' },
  { value: TemplateType.INVOICE, label: 'Invoice' },
  { value: TemplateType.POA, label: 'POA' },
  { value: TemplateType.VAT_GUARANTEE, label: 'VAT guarantee' },
  { value: TemplateType.TRANSIT_REPORT, label: 'Transit report' },
  { value: TemplateType.TRANSIT_FOLLOW_LETTER, label: 'Transit follow letter' },
];

export const templateViewAllOptions: TemplateTypeOption[] = [...templateViewOptions, ...templateViewOtherOptions];

export const ManageTemplates = () => {
  const theme = useMantineTheme();

  const navigate = useNavigate();
  const { data: settings } = useSettings();
  const [searchParams] = useSearchParams();
  const initialCriteria = getInitialTemplateCriteria(searchParams);

  const [filters, setFilters] = useState<TemplatesCriteria['filters']>(initialCriteria.filters);
  const [sorting, setSorting] = useState<TemplatesCriteria['sorting']>(initialCriteria.sorting);
  const [pagination, setPagination] = useState<TemplatesCriteria['pagination']>(initialCriteria.pagination);
  useTemplateCriteriaUrlSync({ filters, sorting, pagination });
  const { data: { list: templatesList, total: totalTemplates } = {}, isLoading: isTemplateListLoading } = useAdminTemplates({ filters, sorting, pagination });
  const [templateView, setTemplateView] = useState<TemplateType | 'other'>(filters.type);
  const [templateViewOther, setTemplateViewOther] = useState(null);
  const { data: declarantsList, isLoading: isDeclarantListLoading } = useDeclarants();
  const [declarantsOptions, setDeclarantsOptions] = useState<ListFilterDropdownElements>({});
  const deleteTemplateMutation = useDeleteTemplate();
  const exportTemplateMutation = useAdminExportTemplate();

  useEffect(() => {
    setDeclarantsOptions(buildDeclarantsDropdownOptions(declarantsList));
  }, [declarantsList, filters.filterByDeclarants]);

  const buildDeclarantsDropdownOptions = (list) => {
    const declarantsDropdownOptionList: ListFilterDropdownElements = {};

    list?.forEach((dec) => {
      declarantsDropdownOptionList[dec._id] = {
        label: dec.accountName,
        checked: filters.filterByDeclarants ? filters.filterByDeclarants.includes(dec._id) : true,
      };
    });

    return declarantsDropdownOptionList;
  };

  const setNewFilter = (field: string, value: string | string[]) => {
    const mergedFilters = removeUndefined({
      ...filters,
      ...{ [field]: value },
    }) as TemplatesCriteria['filters'];

    setFilters(mergedFilters);
    setPagination({ ...pagination, page: 1 });
  };

  const setMultipleFilters = (updatedFilters: { [field: string]: string | string[] }) => {
    const mergedFilters = removeUndefined({
      ...filters,
      ...updatedFilters,
    }) as TemplatesCriteria['filters'];

    setFilters(mergedFilters);
    setPagination({ ...pagination, page: 1 });
  };

  const handleTemplateType = (value: TemplateType | 'other') => {
    setTemplateView(value as any);
    setMultipleFilters({ type: value, searchQuery: '' });
  };

  const handleTemplateTypeOther = (value: TemplateType | 'other') => {
    setTemplateViewOther(value);
    navigate(`/${settings.adminPath}/manage-templates/edit/${value}`);
  };

  const searchTemplate = (searchQuery: string) => {
    setNewFilter('searchQuery', searchQuery);
  };

  const filterByDeclarant = (declarantOption: ListFilterChangeEvent) => {
    if (!declarantsList) {
      return;
    }

    let filterByDeclarants: string[];
    if (!declarantOption.id) {
      if (declarantOption.checked) {
        filterByDeclarants = declarantsList.map((usr) => usr._id);
      } else {
        filterByDeclarants = [];
      }
    } else {
      filterByDeclarants = declarantsList.reduce((acc, cur) => {
        if ((declarantOption.id === cur._id && declarantOption.checked) || (filters.filterByDeclarants?.includes(cur._id) && declarantOption.id !== cur._id)) {
          acc.push(cur._id);
        }
        return acc;
      }, []);
    }

    if (!isEqual(filterByDeclarants, filters.filterByDeclarants)) {
      setNewFilter('filterByDeclarants', filterByDeclarants);
    }
  };

  const handleSorting = (direction, field: string) => {
    setSorting({ field, direction });
    setPagination({ page: 1, size: pagination.size });
  };

  const handlePagination = (page: number, size: number) => {
    setPagination({ page, size });
  };

  const openImportTemplateModal = () => {
    modals.open({
      title: 'Import Template',
      children: <ImportTemplate onClose={() => modals.closeAll()} />,
      size: 'lg',
      centered: true,
    });
  };

  const handleTableAction = async (actionType: string, row?: Template) => {
    switch (actionType) {
      case TableActionEnum.Create: {
        navigate(`/${settings.adminPath}/manage-templates/edit/${templateView}`);
        break;
      }
      case TableActionEnum.Duplicate: {
        modals.open({
          title: 'Duplicate template',
          size: 'lg',
          children: <DuplicateTemplate template={row} />,
        });
        break;
      }
      case TableActionEnum.Edit: {
        navigate(`/${settings.adminPath}/manage-templates/edit/${row.type}/${row._id}`);
        break;
      }
      case TableActionEnum.Delete: {
        modals.openConfirmModal({
          title: 'Confirm template delete',
          children: (
            <Text>
              Are you sure you want to delete the template? <br />
              This action cannot be undone!
            </Text>
          ),
          labels: { confirm: 'Delete', cancel: 'Cancel' },
          confirmProps: { color: 'red' },
          onConfirm: async () => {
            await deleteTemplateMutation.mutateAsync(row._id);
            modals.closeAll();
          },
        });
        break;
      }
      default:
        break;
    }
  };

  const columns = [
    {
      name: 'Template name',
      selector: 'name',
      cell: (row) => row.name,
      sortable: true,
    },
    {
      name: 'Transaction type',
      selector: 'name',
      cell: (row) => row.transactionType?.name,
    },
    {
      name: 'Available',
      selector: 'deleted',
      cell: (row) => <CheckedColumn checked={row.deleted !== true} />,
      sortable: true,
    },
    ...(settings.isForwarder
      ? [
          {
            name: 'Broker',
            selector: 'virtuals.brokerName',
            cell: (row) => row.virtuals?.brokerName,
          },
        ]
      : []),
    {
      name: 'Actions',
      sticky: 'right' as any,
      cell: (row, rowIdx) => (
        <TableActions
          actions={[
            {
              iconComponent: <IconEye color={theme.colors[theme.primaryColor][6]} />,
              text: 'Edit',
              onClick: () => handleTableAction(TableActionEnum.Edit, row),
            },
            {
              iconComponent: <IconCopy color={theme.colors[theme.primaryColor][6]} />,
              text: 'Duplicate',
              onClick: () => handleTableAction(TableActionEnum.Duplicate, row),
            },
            {
              iconComponent: <IconTrash color={theme.colors[theme.primaryColor][6]} />,
              text: 'Delete',
              onClick: () => handleTableAction(TableActionEnum.Delete, row),
            },
            {
              iconComponent: <IconDownload color={theme.colors[theme.primaryColor][6]} />,
              text: 'Export template',
              onClick: () => exportTemplateMutation.mutate(row._id),
            },
          ]}
          disableAfterClick={false}
          moveMenuUpwards={pagination.size - rowIdx <= TABLE_MENU_THRESHOLD}
          data-pw={rowIdx}
        />
      ),
    },
  ];

  if (isTemplateListLoading || isDeclarantListLoading) {
    return (
      <Center style={{ height: '200px' }}>
        <Loader size="xl" />
      </Center>
    );
  }

  return (
    <ContentLayout>
      <ContentLayout.Header>
        <ContentLayout.Title>Manage Templates</ContentLayout.Title>
        <ContentLayout.Filters>
          <Button leftSection={<IconUpload size={16} />} onClick={openImportTemplateModal}>
            Import Template
          </Button>
          <TemplateTypeView templateViewOptions={templateViewOptions} value={templateView} onChange={handleTemplateType} />
          {templateView !== 'other' && (
            <Button
              data-pw="new-template-btn"
              onClick={() => {
                handleTableAction(TableActionEnum.Create);
              }}
              variant="outline"
              size="md"
              h={55}
            >
              New template
            </Button>
          )}
          {templateView === 'other' && (
            <TemplateTypeView
              title="New template"
              templateViewOptions={templateViewOtherOptions}
              value={templateViewOther}
              onChange={handleTemplateTypeOther}
            />
          )}
          <SearchBox handleSearch={searchTemplate} value={filters?.searchQuery} placeholder="Seach by name..." data-pw="template-search-input" />
        </ContentLayout.Filters>
      </ContentLayout.Header>
      <ContentLayout.SubHeader>
        <Group justify="flex-end" mb="lg">
          <ListFilterDropdown title="Declarant filter" values={declarantsOptions} onChange={filterByDeclarant} />
        </Group>
      </ContentLayout.SubHeader>
      <ContentLayout.Content>
        <Table
          columns={columns}
          dataSource={templatesList || []}
          total={totalTemplates}
          onPaginationChange={handlePagination}
          onSortingChange={handleSorting}
          isLoading={isTemplateListLoading || isDeclarantListLoading}
          defaultPage={pagination.page}
          defaultPerPage={pagination.size}
          defaultSorting={sorting.field}
          defaultSortingDirection={sorting.direction}
        />
      </ContentLayout.Content>
    </ContentLayout>
  );
};
